import React from 'react';
import TabContext from '@mui/lab/TabContext';

import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { TabList, TabPanel } from '@mui/lab';
import { Divider, Tab } from '@mui/material';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { GatherUsingAINodeState, NodeFieldType } from '../../types';
import { ToolArguments } from '@common/Node/NodeSidebar/Custom';
import { WebhookData } from '@common/Node/NodeSidebar/NodeData';
import { ArticleOutlined, SecurityOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../../styles';
import { getModelKey, ModelAuthKey } from '@common/NodeAuth/ModelAuthKey';
import { useParams } from 'react-router-dom';
import { PollyVoice } from '@common/Node/NodeSidebar/Custom/TTSVoices/PollyVoice';
import { Toggle } from '@common/Node/NodeSidebar/Custom/Toggle';
import { AzureVoice } from '@common/Node/NodeSidebar/Custom/TTSVoices/AzureVoice';
import { useSecrets } from '@common/hooks/useSecrets';
import { KokoroVoice } from '@common/Node/NodeSidebar/Custom/TTSVoices/KokoroVoice';
import { ElevenLabsVoice } from '@common/Node/NodeSidebar/Custom/TTSVoices/ElevenLabsVoice';
import { isPaidModel } from '@common/Autocomplete/Base';

interface Props {
  tab: string;
  setTab: React.Dispatch<string>;
}

export const GatherUsingAITabs: React.FC<Props> = ({ tab, setTab }) => {
  const theme = useTheme();
  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);
  const themeType = useSelector((state: State) => state.flow.theme);
  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);

  const { workflow_id } = useParams();
  const { secrets, refetch, error, loading } = useSecrets();

  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return;

  const state = command.state as GatherUsingAINodeState;
  const model = state?.model || '';

  const modelKey = getModelKey(model);
  const secretName = modelKey ? authentication?.[modelKey] : null;
  const authRequired = (!secretName || !secrets.find((s) => s.identifier === secretName)) && isPaidModel(model);

  const fields = command.fields;
  const voiceInputs = fields.filter((field) => field.tab === '2');
  const gatherArgumentField = fields.find((field) => field.type === NodeFieldType.ToolArgs);
  const tabColor = themeType === 'light' ? theme.text.standard : theme.telnyx.citron;

  const inputs = fields.filter((field) => {
    if (field.id === 'instructions' && !state?.model) return false;
    if (field.id === 'message_history') return false;
    if (field.id === 'client_state') return false;
    if (field.tab !== '1') return false;
    return true;
  });

  const extraInputs = fields.filter((field) => {
    if (field.id === 'message_history') return true;
    if (field.id === 'client_state') return true;
    return false;
  });

  const isTelnyxVoice = state?.voice?.value === 'Telnyx.LibriTTS.0';

  const transcriptionModelField = fields.find((field) => field.id === 'transcription_model');
  const languageGoogleField = fields.find((field) => field.id === 'language_google');
  const partialResultsField = fields.find((field) => field.id === 'send_partial_results');
  const enabledInterruptField = fields.find((field) => field.id === 'interruption_enabled');

  const telnyxVoiceSettings = fields.filter((field) => field.tab === 'telnyx_voice');

  const secondTabError = nodeErrors?.find(
    (nodeError) => nodeError?.workflowId === workflow_id && nodeError.fields.some((field) => field.tab === '2')
  );

  return (
    <NodeSidebar>
      <TabContext value={authRequired ? '4' : tab}>
        <NodeSidebarTabsContainer color={tabColor}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' disabled={authRequired} />
            <Tab
              label='Voice'
              disabled={authRequired}
              disableRipple
              value='2'
              sx={{ color: Boolean(secondTabError) ? `${theme.telnyx.red} !important` : undefined }}
            />
            <Tab
              title='Data'
              disabled={authRequired}
              disableRipple
              iconPosition='start'
              icon={<ArticleOutlined />}
              value='3'
            />
            <Tab
              title='Auth'
              disabled={!isPaidModel(model)}
              disableRipple
              iconPosition='start'
              icon={<SecurityOutlined />}
              value='4'
            />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {inputs.map((field) => (
              <NodeInputExpression key={field.id} command={command} field={field} />
            ))}
            {gatherArgumentField && <ToolArguments command={command} field={gatherArgumentField} />}
            <Divider />
            {partialResultsField && <Toggle command={command} field={partialResultsField} />}
            {enabledInterruptField && <Toggle command={command} field={enabledInterruptField} />}
            {transcriptionModelField && <NodeInputExpression command={command} field={transcriptionModelField} />}
            {state.transcription_model === 'google' && languageGoogleField && (
              <NodeInputExpression command={command} field={languageGoogleField} />
            )}
            {extraInputs.map((field) => (
              <NodeInputExpression key={field.id} command={command} field={field} />
            ))}
            <Divider sx={{ marginBottom: '4rem', marginTop: '0.5rem' }} />
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='2'>
          <NodeSidebarTab>
            {voiceInputs.map((field) => (
              <NodeInputExpression key={field.id} command={command} field={field} />
            ))}
            {isTelnyxVoice &&
              telnyxVoiceSettings.map((field) => (
                <NodeInputExpression key={field.id} command={command} field={field} />
              ))}
            <ElevenLabsVoice command={command} fields={fields} />
            <KokoroVoice command={command} fields={fields} />
            <PollyVoice command={command} fields={fields} />
            <AzureVoice command={command} fields={fields} />
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='3'>
          <WebhookData command={command} />
        </TabPanel>
        <TabPanel value='4'>
          <NodeSidebarTab>
            <ModelAuthKey
              command={command}
              secrets={secrets}
              secretsLoading={loading}
              secretsError={error}
              refetch={refetch}
              setTab={setTab}
            />
          </NodeSidebarTab>
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
